import { useState } from "react";

import moment from "moment";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import { formatDate } from "react-lib/utils/dateUtils";

import { AlertV4, ButtonV4, FieldV4, ModalV4 } from "../common/ui";
import { CHECKOUT_TYPES, deathDatetimeOf, type CheckoutCause } from "./checkoutModelV4";

// Types
type ModalCheckoutV4Props = {
  saving?: boolean;
  error?: unknown;
  onConfirm: (cause: CheckoutCause, note: string, deathDatetime: string) => void;
  onClose: () => void;
};

const ModalCheckoutV4 = (props: ModalCheckoutV4Props) => {
  const [cause, setCause] = useState<CheckoutCause | "">("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const [deathDate, setDeathDate] = useState(() => formatDate(moment()));
  const [deathTime, setDeathTime] = useState(() => moment().format("HH:mm"));
  const isDead = cause === "DEAD";
  const deathDatetime = isDead ? deathDatetimeOf(deathDate, deathTime) : "";
  const ready = !!cause && (!isDead || !!deathDatetime);

  // Handler
  const handleConfirm = () => {
    if (!cause || !ready) return;
    setSent(true);
    props.onConfirm(cause, note.trim(), deathDatetime);
  };

  // Render
  return (
    <ModalV4 title="Check Out" width={620} onClose={props.onClose}>
      <div className="hv4-co-body">
        <div className="hv4-co-sub">
          เลือกรูปแบบการ Check out · หลังยืนยัน สถานะจะเปลี่ยนเป็น "รอชำระเงิน"
        </div>
        <div role="radiogroup" aria-label="รูปแบบการ Check out" className="hv4-co-opts">
          {CHECKOUT_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              role="radio"
              aria-checked={cause === type.value}
              className={`hv4-co-opt${cause === type.value ? " hv4-co-opt--on" : ""}`}
              onClick={() => setCause(type.value)}
            >
              <span className="hv4-co-dot" aria-hidden="true" />
              <span className="hv4-co-opt-text">
                <b>{type.label}</b>
                <span>{type.desc}</span>
              </span>
            </button>
          ))}
        </div>
        {isDead && (
          <div className="hv4-co-death">
            <FieldV4 label="วันที่เสียชีวิต *" htmlFor="hv4_co_death_date">
              <div className="hv4-date">
                <DateTextBox
                  id="hv4_co_death_date"
                  placeholder="DD/MM/YYYY"
                  value={deathDate}
                  onChange={(date: string) => setDeathDate(date)}
                />
              </div>
            </FieldV4>
            <FieldV4 label="เวลาที่เสียชีวิต *" htmlFor="hv4_co_death_time">
              <input
                id="hv4_co_death_time"
                type="time"
                className="hv4-input"
                value={deathTime}
                onChange={(e) => setDeathTime(e.target.value)}
              />
            </FieldV4>
          </div>
        )}
        <FieldV4
          label={cause === "REFER_OUT" ? "สถานพยาบาลปลายทาง / หมายเหตุ" : "หมายเหตุ (ถ้ามี)"}
          htmlFor="hv4_co_note"
        >
          <input
            id="hv4_co_note"
            className="hv4-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FieldV4>
        <AlertV4 tone="danger" error={sent ? props.error : null} />
        <div className="hv4-co-foot">
          <ButtonV4 variant="ghost" onClick={props.onClose}>
            ยกเลิก
          </ButtonV4>
          <ButtonV4
            variant="primary"
            className="hv4-co-ok"
            disabled={!ready || props.saving}
            onClick={handleConfirm}
          >
            Checkout
          </ButtonV4>
        </div>
      </div>
    </ModalV4>
  );
};

ModalCheckoutV4.displayName = "ModalCheckoutV4";

export default ModalCheckoutV4;
